import { AddonInstance, ImageName } from 'api/models';
import {
  addDays,
  differenceInDays,
  formatDistanceToNowStrict,
  format,
  isBefore,
  subDays,
  subYears,
} from 'date-fns';
import { CLUSTERS } from './version_register_cp';
import { AccountAddon } from 'api/models';

// Ref: https://github.com/chkk-io/addon-hub/blob/main/pkg/addons/sources/eol_detection_utils.go#L42
// From API contract: 3 means EOL date is not provided but can be safely assumed to be in the future.
export const EOL_DATE_NOT_PROVIDED = 3;

export function getAddonExpiryInWords(
  instances: AddonInstance[] | AccountAddon[],
): string | undefined {
  // landing page gives all instances for a single addon_id
  // view_addons page gives all instances for a given primary comp image tag

  const expiryDates = instances
    .filter((c) => getEOLDate(c) !== undefined)
    .map((c) => getEOLDate(c) || 0);
  if (expiryDates.length === 0) {
    return undefined;
  }

  const earliestExpiry: number = Math.min(...expiryDates);
  if (earliestExpiry === 0 || earliestExpiry === EOL_DATE_NOT_PROVIDED) {
    return undefined;
  } else if (earliestExpiry === 1) {
    return 'more than a year ago';
  } else if (earliestExpiry === 2) {
    return 'Date not provided by maintainers';
  } else {
    const expiryDate = new Date(Math.min(...expiryDates) * 1000);
    if (isBefore(expiryDate, subYears(new Date(), 1))) {
      return 'more than a year ago';
    } else {
      return (
        formatDistanceToNowStrict(expiryDate, { addSuffix: true }) +
        format(expiryDate, ' (LLL yyyy)')
      );
    }
  }
}

function getEOLDate(
  instance: AddonInstance | AccountAddon,
): number | undefined {
  if ('eol_date' in instance) {
    return instance.eol_date;
  } else if ('earliest_eol_date' in instance) {
    return instance.earliest_eol_date;
  } else {
    return undefined;
  }
}

export function getEarliestAddonExpiryInDays(
  instances: AddonInstance[] | AccountAddon[],
): number | undefined {
  const expiryDates = instances
    .map((c) => getEOLDate(c) || 0)
    .filter((d) => d > 0);
  if (expiryDates.length === 0) {
    return undefined;
  }

  return differenceInDays(
    new Date(Math.min(...expiryDates) * 1000),
    new Date(),
  );
}

export function getAddonExpiryState(
  instances: AddonInstance[] | AccountAddon[],
): 'default' | 'warning' | 'error' {
  const earliestExpiry = getEarliestAddonExpiryInDays(instances);
  if (earliestExpiry === undefined || earliestExpiry > 90) {
    return 'default';
  } else if (earliestExpiry >= 0) {
    return 'warning';
  } else {
    return 'error';
  }
}

export function getHumanReadableImageTag(
  imageName: ImageName | undefined,
): string {
  if (imageName?.tag !== undefined) {
    return imageName.tag;
  } else if (imageName?.digest !== undefined) {
    return `${imageName.digest.slice(0, 7 + 6)}`;
  } else {
    return 'Unknown';
  }
}

// Takes an array of cluster IDs and an array of addon instances and assigns
// each addon instance a cluster ID. If an addon instance already has a cluster
// ID, it is left unchanged.
function distributeClusterIdsToAddonInstances(
  clusterIds: string[],
  addons: AddonInstance[],
): void {
  addons.forEach((addon, index) => {
    addon.cluster_id =
      addon.cluster_id === ''
        ? clusterIds[index % clusterIds.length]
        : addon.cluster_id;
  });
}

// https://github.com/chkk-io/sprints/issues/2959
// We keep the eol dates dynamic to always show both
// yellow and red clocks on the dashboard
export const ADDONS: AddonInstance[] = [
  // Istio 1.21.3 & 1.22.5
  {
    id: 'addinst_54d5a2db-144e-40eb-a828-5757dddec241',
    addon_id: 'addon_cbdaa82d-3360-4bc7-a2b6-0978d11e96d4',
    cluster_id: '',
    version: '1.21.3',
    primary_component: {
      image: {
        registry_url: 'docker.io/istio/pilot',
        tag: '1.21.3',
      },
    },
    resources: [
      {
        name: 'istiod',
        type: 'Deployment',
        namespace: 'istio-system',
        images: [
          {
            registry_url: 'docker.io/istio/pilot',
            tag: '1.21.3',
          },
        ],
      },
      {
        name: 'istio-ingressgateway',
        type: 'Deployment',
        namespace: 'istio-system',
        images: [
          {
            registry_url: 'docker.io/istio/proxyv2',
            tag: '1.21.3',
          },
        ],
      },
    ],
    eol_date: new Date(2024, 8, 27).getTime() / 1000,
  },
  {
    id: 'addinst_istio-2',
    addon_id: 'addon_cbdaa82d-3360-4bc7-a2b6-0978d11e96d4',
    cluster_id: '',
    version: '1.22.5',
    primary_component: {
      image: {
        registry_url: 'docker.io/istio/pilot',
        tag: '1.22.5',
      },
    },
    resources: [
      {
        name: 'istiod',
        type: 'Deployment',
        namespace: 'istio-system',
        images: [
          {
            registry_url: 'docker.io/istio/pilot',
            tag: '1.22.5',
          },
        ],
      },
      {
        name: 'istio-ingressgateway',
        type: 'Deployment',
        namespace: 'istio-system',
        images: [
          {
            registry_url: 'docker.io/istio/proxyv2',
            tag: '1.22.5',
          },
        ],
      },
    ],
    eol_date: new Date(2025, 0, 22).getTime() / 1000,
  },
  {
    id: 'addinst_istio-3',
    addon_id: 'addon_cbdaa82d-3360-4bc7-a2b6-0978d11e96d4',
    cluster_id: 'id-eks-130-use2-staging',
    version: '1.22.5',
    primary_component: {
      image: {
        registry_url: 'docker.io/istio/pilot',
        tag: '1.22.5',
      },
    },
    resources: [
      {
        name: 'istiod',
        type: 'Deployment',
        namespace: 'istio-system',
        images: [
          {
            registry_url: 'docker.io/istio/pilot',
            tag: '1.22.5',
          },
        ],
      },
      {
        name: 'istio-ingressgateway',
        type: 'Deployment',
        namespace: 'istio-system',
        images: [
          {
            registry_url: 'docker.io/istio/proxyv2',
            tag: '1.22.5',
          },
        ],
      },
    ],
    eol_date: new Date(2025, 0, 22).getTime() / 1000, // 2025-01-22
  },

  // RabbitMQ 3.12.11
  {
    id: 'addinst_a5bb9899-1c85-4bc8-925d-b761be654b00',
    addon_id: 'addon_776b979a-efc4-4a60-9c3d-2fb21b6b692f',
    cluster_id: '',
    version: '3.12.11',
    primary_component: {
      image: {
        registry_url: 'docker.io/rabbitmq',
        tag: '3.12.11',
      },
    },
    resources: [
      {
        name: 'rabbitmq',
        type: 'StatefulSet',
        namespace: 'rabbitmq',
        images: [
          {
            registry_url: 'docker.io/rabbitmq',
            tag: '3.12.11',
          },
        ],
      },
    ],
    eol_date: new Date(2024, 1, 22).getTime() / 1000, // 2024-02-22
  },

  // External Secrets Operator 0.7.1, 0.8.0 & 0.9.16
  {
    id: 'addinst_eso-1',
    addon_id: 'addon_0f9f596d-9bf6-40ed-b95e-a21ab4d3dd58',
    cluster_id: '',
    version: '0.7.1',
    primary_component: {
      image: {
        registry_url: 'ghcr.io/external-secrets/external-secrets',
        tag: '0.7.1',
      },
    },
    resources: [
      {
        name: 'external-secrets',
        type: 'Deployment',
        namespace: 'external-secrets',
        images: [
          {
            registry_url: 'ghcr.io/external-secrets/external-secrets',
            tag: '0.7.1',
          },
        ],
      },
    ],
    eol_date: new Date(2023, 5, 22).getTime() / 1000, // 2023-06-22
  },
  {
    id: 'addinst_eso-2',
    addon_id: 'addon_0f9f596d-9bf6-40ed-b95e-a21ab4d3dd58',
    cluster_id: 'id-eks-130-use2-staging',
    version: '0.8.0',
    primary_component: {
      image: {
        registry_url: 'ghcr.io/external-secrets/external-secrets',
        tag: '0.8.0',
      },
    },
    resources: [
      {
        name: 'external-secrets',
        type: 'Deployment',
        namespace: 'external-secrets',
        images: [
          {
            registry_url: 'ghcr.io/external-secrets/external-secrets',
            tag: '0.8.0',
          },
        ],
      },
    ],
    eol_date: new Date(2024, 7, 3).getTime() / 1000, // 2024-08-03
  },
  {
    id: 'addinst_eso-3',
    addon_id: 'addon_0f9f596d-9bf6-40ed-b95e-a21ab4d3dd58',
    cluster_id: 'id-gke-132-euw2-staging',
    version: '0.9.16',
    primary_component: {
      image: {
        registry_url: 'ghcr.io/external-secrets/external-secrets',
        tag: '0.9.16',
      },
    },
    resources: [
      {
        name: 'external-secrets',
        type: 'Deployment',
        namespace: 'external-secrets',
        images: [
          {
            registry_url: 'ghcr.io/external-secrets/external-secrets',
            tag: '0.9.16',
          },
        ],
      },
    ],
    eol_date: new Date(2024, 11, 2).getTime() / 1000, // 2024-12-2
  },

  // ArgoCD 2.11.0
  {
    id: 'addinst_argocd-1',
    addon_id: 'addon_30e39ed1-5e82-4806-af53-92866f4b25e0',
    cluster_id: '',
    version: '2.11.0',
    primary_component: {
      image: {
        registry_url: 'quay.io/argoproj/argocd',
        tag: 'v2.11.0',
      },
    },
    resources: [
      {
        name: 'argocd-server',
        type: 'Deployment',
        namespace: 'argocd',
        images: [
          {
            registry_url: 'quay.io/argoproj/argocd',
            tag: 'v2.11.0',
          },
        ],
      },
    ],
    eol_date: new Date(2025, 1, 3).getTime() / 1000, // 2025-02-03
  },

  // Karpenter 0.31.1 & 0.37.7
  {
    id: 'addinst_karpenter-1',
    addon_id: 'addon_fef30be6-1088-4545-93c3-5804b112dd84',
    cluster_id: '',
    version: 'v0.31.1',
    primary_component: {
      image: {
        registry_url: 'public.ecr.aws/karpenter/controller',
        tag: 'v0.31.1',
      },
    },
    resources: [
      {
        name: 'karpenter',
        type: 'Deployment',
        namespace: 'karpenter',
        images: [
          {
            registry_url: 'public.ecr.aws/karpenter/controller',
            tag: 'v0.31.1',
          },
        ],
      },
    ],
    eol_date: new Date(2023, 9, 31).getTime() / 1000, // 2023-10-31
  },
  {
    id: 'addinst_karpenter-3',
    addon_id: 'addon_fef30be6-1088-4545-93c3-5804b112dd84',
    cluster_id: 'id-eks-130-use2-staging',
    version: 'v0.37.7',
    primary_component: {
      image: {
        registry_url: 'public.ecr.aws/karpenter/controller',
        tag: 'v0.37.7',
      },
    },
    resources: [
      {
        name: 'karpenter',
        type: 'Deployment',
        namespace: 'kube-system',
        images: [
          {
            registry_url: 'public.ecr.aws/karpenter/controller',
            tag: 'v0.37.7', // release Feb 1, 2025
          },
        ],
      },
    ],
    eol_date: new Date(2024, 7, 15).getTime() / 1000, // 2024-08-15
  },

  // cert-manager 0.13.1, v1.14.1 & v1.14.4
  {
    id: 'addinst_cert-manager-1',
    addon_id: 'addon_d7191da5-c5f3-45f7-a620-38d434dd5096',
    cluster_id: '',
    version: 'v1.14.1',
    primary_component: {
      image: {
        registry_url: 'quay.io/jetstack/cert-manager-controller',
        tag: 'v1.14.1',
      },
    },
    resources: [
      {
        name: 'cert-manager',
        type: 'Deployment',
        namespace: 'cert-manager',
        images: [
          {
            registry_url: 'quay.io/jetstack/cert-manager-controller',
            tag: 'v1.14.1',
          },
        ],
      },
      {
        name: 'cert-manager-cainjector',
        type: 'Deployment',
        namespace: 'cert-manager',
        images: [
          {
            registry_url: 'quay.io/jetstack/cert-manager-cainjector',
            tag: 'v1.14.1',
          },
        ],
      },
      {
        name: 'cert-manager-webhook',
        type: 'Deployment',
        namespace: 'cert-manager',
        images: [
          {
            registry_url: 'quay.io/jetstack/cert-manager-webhook',
            tag: 'v1.14.1',
          },
        ],
      },
    ],
    eol_date: new Date(2024, 9, 3).getTime() / 1000, // 2024-10-03
  },
  {
    id: 'addinst_cert-manager-2',
    addon_id: 'addon_d7191da5-c5f3-45f7-a620-38d434dd5096',
    cluster_id: 'id-eks-130-use2-staging',
    version: 'v1.13.1',
    primary_component: {
      image: {
        registry_url: 'quay.io/jetstack/cert-manager-controller',
        tag: 'v1.13.1',
      },
    },
    resources: [
      {
        name: 'cert-manager',
        type: 'Deployment',
        namespace: 'cert-manager',
        images: [
          {
            registry_url: 'quay.io/jetstack/cert-manager-controller',
            tag: 'v1.13.1',
          },
        ],
      },
      {
        name: 'cert-manager-cainjector',
        type: 'Deployment',
        namespace: 'cert-manager',
        images: [
          {
            registry_url: 'quay.io/jetstack/cert-manager-cainjector',
            tag: 'v1.13.1',
          },
        ],
      },
      {
        name: 'cert-manager-webhook',
        type: 'Deployment',
        namespace: 'cert-manager',
        images: [
          {
            registry_url: 'quay.io/jetstack/cert-manager-webhook',
            tag: 'v1.13.1',
          },
        ],
      },
    ],
    eol_date: new Date(2024, 5, 5).getTime() / 1000, // 2024-06-05
  },
  {
    id: 'addinst_cert-manager-3',
    addon_id: 'addon_d7191da5-c5f3-45f7-a620-38d434dd5096',
    cluster_id: 'id-aks-131-cus1-dev',
    version: 'v1.16.0',
    primary_component: {
      image: {
        registry_url: 'quay.io/jetstack/cert-manager-controller',
        tag: 'v1.16.0',
      },
    },
    resources: [
      {
        name: 'cert-manager',
        type: 'Deployment',
        namespace: 'cert-manager',
        images: [
          {
            registry_url: 'quay.io/jetstack/cert-manager-controller',
            tag: 'v1.16.0',
          },
        ],
      },
      {
        name: 'cert-manager-cainjector',
        type: 'Deployment',
        namespace: 'cert-manager',
        images: [
          {
            registry_url: 'quay.io/jetstack/cert-manager-cainjector',
            tag: 'v1.16.0',
          },
        ],
      },
      {
        name: 'cert-manager-webhook',
        type: 'Deployment',
        namespace: 'cert-manager',
        images: [
          {
            registry_url: 'quay.io/jetstack/cert-manager-webhook',
            tag: 'v1.16.0',
          },
        ],
      },
    ],
    eol_date: new Date(2024, 9, 3).getTime() / 1000, // 2024-10-03
  },

  // CoreDNS 1.8.4-eksbuild-1
  {
    id: 'addinst_coredns-1',
    addon_id: 'addon_b692878c-4298-4f61-8100-df19363b0ed6',
    cluster_id: 'id-eks-131-usw1-prod',
    version: 'v1.8.4-eksbuild.1',
    primary_component: {
      image: {
        registry_url:
          '602401143452.dkr.ecr.us-west-2.amazonaws.com/eks/coredns',
        tag: 'v1.8.4-eksbuild.1',
      },
    },
    resources: [
      {
        name: 'coredns',
        type: 'Deployment',
        namespace: 'kube-system',
        images: [
          {
            registry_url:
              '602401143452.dkr.ecr.us-west-2.amazonaws.com/eks/coredns',
            tag: 'v1.8.4-eksbuild.1',
          },
        ],
      },
    ],
    // no eol date
  },

  // KubeProxy 1.26.15-eksbuild.35
  {
    id: 'addinst_kubeproxy-1',
    addon_id: 'addon_143085db-4ed1-46da-ad99-0e79868d44ca',
    cluster_id: 'id-eks-131-usw1-prod',
    version: 'v1.26.15-eksbuild.35',
    primary_component: {
      image: {
        registry_url:
          '602401143452.dkr.ecr.us-west-2.amazonaws.com/eks/kube-proxy',
        tag: 'v1.26.15-eksbuild.35',
      },
    },
    resources: [
      {
        name: 'kube-proxy',
        type: 'DaemonSet',
        namespace: 'kube-system',
        images: [
          {
            registry_url:
              '602401143452.dkr.ecr.us-west-2.amazonaws.com/eks/kube-proxy',
            tag: 'v1.26.15-eksbuild.35',
          },
        ],
      },
    ],
    // no eol date
  },

  // Vault Secrets Operator 0.9.0
  {
    id: 'addinst_vault-secrets-operator-1',
    addon_id: 'addon_e79277b6-3549-428f-b3c8-08cabe46c878',
    cluster_id: '',
    version: '0.9.0',
    primary_component: {
      image: {
        registry_url: 'hashicorp/vault-secrets-operator',
        tag: '0.9.0',
      },
    },
    resources: [
      {
        name: 'vault-secrets-operator',
        type: 'Deployment',
        namespace: 'vault-secrets-operator',
        images: [
          {
            registry_url: 'hashicorp/vault-secrets-operator',
            tag: '0.9.0',
          },
        ],
      },
    ],
    // no eol date
  },

  // Nginx OSS 1.24.0-alpine
  {
    id: 'addinst_nginx-oss-1',
    addon_id: 'addon_1e79922d-eb21-4cf6-ac64-08b8eb7ef24c',
    cluster_id: '',
    version: '1.24.0-alpine',
    primary_component: {
      image: {
        registry_url: 'nginx',
        tag: '1.24.0-alpine',
      },
    },
    resources: [
      {
        name: 'nginx',
        type: 'Deployment',
        namespace: 'nginx',
        images: [
          {
            registry_url: 'nginx',
            tag: '1.24.0-alpine',
          },
        ],
      },
    ],
    eol_date: new Date(2026, 5, 30).getTime() / 1000, // 2026-06-30
  },
  {
    id: 'addinst_nginx-oss-2',
    addon_id: 'addon_1e79922d-eb21-4cf6-ac64-08b8eb7ef24c',
    cluster_id: '',
    version: '1.24.0-alpine',
    primary_component: {
      image: {
        registry_url: 'nginx',
        tag: '1.24.0-alpine',
      },
    },
    resources: [
      {
        name: 'nginx',
        type: 'Deployment',
        namespace: 'nginx',
        images: [
          {
            registry_url: 'nginx',
            tag: '1.24.0-alpine',
          },
        ],
      },
    ],
    eol_date: new Date(2026, 5, 30).getTime() / 1000, // 2026-06-30
  },

  // ACK IAM Controller v0.0.19
  {
    id: 'addinst_ack-iam-ctrl-1',
    addon_id: 'addon_a685087b-c883-4831-9e05-f417bee79fbc',
    cluster_id: '',
    version: 'v0.0.19',
    primary_component: {
      image: {
        registry_url: 'public.ecr.aws/aws-controllers-k8s/iam-controller',
        tag: 'v0.0.19',
      },
    },
    resources: [
      {
        name: 'ack-iam-controller',
        type: 'Deployment',
        namespace: 'ack-system',
        images: [
          {
            registry_url: 'public.ecr.aws/aws-controllers-k8s/iam-controller',
            tag: 'v0.0.19',
          },
        ],
      },
    ],
    // no eol date
  },

  // Apache HTTP Server 2.4-alpine
  {
    id: 'addinst_apache-http-1',
    addon_id: 'addon_4e82b060-2725-4e84-9cec-0d1aa04c10ff',
    cluster_id: '',
    version: '2.4-alpine',
    primary_component: {
      image: {
        registry_url: 'docker.io/httpd',
        tag: '2.4-alpine',
      },
    },
    resources: [
      {
        name: 'apache-httpd',
        type: 'Deployment',
        namespace: 'web',
        images: [
          {
            registry_url: 'docker.io/httpd',
            tag: '2.4-alpine',
          },
        ],
      },
    ],
    // no eol date // Apache httpd 2.4 is the current LTS and does not have a public EOL
  },

  // Apollo Router v1.61.3
  {
    id: 'addinst_apollo-router-1',
    addon_id: 'addon_1dad04eb-5da9-4894-b37a-1f02de2e12ee',
    cluster_id: '',
    version: 'v1.61.3',
    primary_component: {
      image: {
        registry_url: 'ghcr.io/apollographql/router',
        tag: 'v1.61.3',
      },
    },
    resources: [
      {
        name: 'apollo-router',
        type: 'Deployment',
        namespace: 'apollo',
        images: [
          {
            registry_url: 'ghcr.io/apollographql/router',
            tag: 'v1.61.3',
          },
        ],
      },
    ],
    // no eol date
  },
  {
    id: 'addinst_apollo-router-2',
    addon_id: 'addon_apollo-router-uuid1',
    cluster_id: '',
    version: 'v1.61.3',
    primary_component: {
      image: {
        registry_url: 'ghcr.io/apollographql/router',
        tag: 'v1.61.3',
      },
    },
    resources: [
      {
        name: 'apollo-router',
        type: 'Deployment',
        namespace: 'apollo',
        images: [
          {
            registry_url: 'ghcr.io/apollographql/router',
            tag: 'v1.61.3',
          },
        ],
      },
    ],
    // no eol date
  },

  // Argo Rollouts v1.7.2
  {
    id: 'addinst_argo-rollouts-1',
    addon_id: 'addon_fd1b3544-c921-4564-82b2-7caf7dba3f09',
    cluster_id: '',
    version: 'v1.7.2',
    primary_component: {
      image: {
        registry_url: 'quay.io/argoproj/argo-rollouts',
        tag: 'v1.7.2',
      },
    },
    resources: [
      {
        name: 'argo-rollouts',
        type: 'Deployment',
        namespace: 'argo-rollouts',
        images: [
          {
            registry_url: 'quay.io/argoproj/argo-rollouts',
            tag: 'v1.7.2',
          },
        ],
      },
    ],
    // no eol date // Argo Rollouts 1.7.x is latest as of June 2025, no EOL announced
  },

  // Argo Workflows v3.5.5
  {
    id: 'addinst_argo-workflows-1',
    addon_id: 'addon_dda0b12f-9a76-43ff-a024-9839ed3ee57b',
    cluster_id: '',
    version: 'v3.5.5',
    primary_component: {
      image: {
        registry_url: 'quay.io/argoproj/workflow-controller',
        tag: 'v3.5.5',
      },
    },
    resources: [
      {
        name: 'argo-workflows',
        type: 'Deployment',
        namespace: 'argo',
        images: [
          {
            registry_url: 'quay.io/argoproj/workflow-controller',
            tag: 'v3.5.5',
          },
        ],
      },
    ],
    // no eol date // Argo Workflows 3.5.x is current and supported
  },

  // Cilium v1.16.6
  {
    id: 'addinst_cilium-1',
    addon_id: 'addon_fdd78e80-073e-445f-96cd-bdd4fa94a6f3',
    cluster_id: '',
    version: 'v1.16.6',
    primary_component: {
      image: {
        registry_url: 'quay.io/cilium/cilium',
        tag: 'v1.16.6',
      },
    },
    resources: [
      {
        name: 'cilium',
        type: 'DaemonSet',
        namespace: 'kube-system',
        images: [
          {
            registry_url: 'quay.io/cilium/cilium',
            tag: 'v1.16.6',
          },
        ],
      },
    ],
    // no eol date // Cilium 1.16.x is current as of June 2025, no EOL announced
  },

  // Cilium Operator v1.16.6
  {
    id: 'addinst_cilium-operator-1',
    addon_id: 'addon_66f72d70-c807-4cc1-9fc7-f5995c3ada72',
    cluster_id: '',
    version: 'v1.16.6',
    primary_component: {
      image: {
        registry_url: 'quay.io/cilium/operator-generic',
        tag: 'v1.16.6',
      },
    },
    resources: [
      {
        name: 'cilium-operator',
        type: 'Deployment',
        namespace: 'kube-system',
        images: [
          {
            registry_url: 'quay.io/cilium/operator-generic',
            tag: 'v1.16.6',
          },
        ],
      },
    ],
    // no eol date // Cilium 1.16.x is current as of June 2025, no EOL announced
  },

  // Datadog Agent v7.64.3
  {
    id: 'addinst_datadog-agent-1',
    addon_id: 'addon_5888c383-2d4e-4fe4-ab83-292587895e2b',
    cluster_id: '',
    version: '7.64.3',
    primary_component: {
      image: {
        registry_url: 'gcr.io/datadoghq/agent',
        tag: '7.64.3',
      },
    },
    resources: [
      {
        name: 'datadog-agent',
        type: 'DaemonSet',
        namespace: 'datadog',
        images: [
          {
            registry_url: 'gcr.io/datadoghq/agent',
            tag: '7.64.3',
          },
        ],
      },
    ],
    eol_date: new Date(2025, 4, 6).getTime() / 1000, // 2025-05-06
  },
  {
    id: 'addinst_datadog-agent-2',
    addon_id: 'addon_5888c383-2d4e-4fe4-ab83-292587895e2b',
    cluster_id: '',
    version: '7.64.1-jmx',
    primary_component: {
      image: {
        registry_url: 'gcr.io/datadoghq/agent',
        tag: '7.64.1-jmx',
      },
    },
    resources: [
      {
        name: 'datadog-agent',
        type: 'DaemonSet',
        namespace: 'datadog',
        images: [
          {
            registry_url: 'gcr.io/datadoghq/agent',
            tag: '7.64.1-jmx',
          },
        ],
      },
    ],
    eol_date: new Date(2025, 4, 6).getTime() / 1000, // 2025-05-06
  },

  // Grafana v10.4.1
  {
    id: 'addinst_grafana-1',
    addon_id: 'addon_89549157-9fbe-4297-90f2-11122c700aee',
    cluster_id: '',
    version: '10.4.1',
    primary_component: {
      image: {
        registry_url: 'grafana/grafana',
        tag: '10.4.1',
      },
    },
    resources: [
      {
        name: 'grafana',
        type: 'Deployment',
        namespace: 'grafana',
        images: [
          {
            registry_url: 'grafana/grafana',
            tag: '10.4.1',
          },
        ],
      },
    ],
    // no eol date
  },

  // Kube RBAC Proxy v0.18.1
  {
    id: 'addinst_kube-rbac-proxy-1',
    addon_id: 'addon_edc1e7ab-53fa-4ea1-a78d-0b8aa9e36dce',
    cluster_id: '',
    version: 'v0.18.1',
    primary_component: {
      image: {
        registry_url: 'quay.io/brancz/kube-rbac-proxy',
        tag: 'v0.18.1',
      },
    },
    resources: [
      {
        name: 'kube-rbac-proxy',
        type: 'Deployment',
        namespace: 'monitoring',
        images: [
          {
            registry_url: 'quay.io/brancz/kube-rbac-proxy',
            tag: 'v0.18.1',
          },
        ],
      },
    ],
    // no eol date
  },
  {
    id: 'addinst_kube-rbac-proxy-2',
    addon_id: 'addon_edc1e7ab-53fa-4ea1-a78d-0b8aa9e36dce',
    cluster_id: '',
    version: 'v0.8.0',
    primary_component: {
      image: {
        registry_url: 'quay.io/brancz/kube-rbac-proxy',
        tag: 'v0.8.0',
      },
    },
    resources: [
      {
        name: 'kube-rbac-proxy',
        type: 'Deployment',
        namespace: 'monitoring',
        images: [
          {
            registry_url: 'quay.io/brancz/kube-rbac-proxy',
            tag: 'v0.8.0',
          },
        ],
      },
    ],
    // no eol date
  },

  // K8s Metrics Server v0.7.1
  {
    id: 'addinst_k8s-metrics-server-1',
    addon_id: 'addon_767ffeae-6c39-4cf9-bd1f-0ceaf9f32760',
    cluster_id: '',
    version: 'v0.7.1',
    primary_component: {
      image: {
        registry_url: 'k8s.gcr.io/metrics-server/metrics-server',
        tag: 'v0.7.1',
      },
    },
    resources: [
      {
        name: 'metrics-server',
        type: 'Deployment',
        namespace: 'kube-system',
        images: [
          {
            registry_url: 'k8s.gcr.io/metrics-server/metrics-server',
            tag: 'v0.7.1',
          },
        ],
      },
    ],
    // no eol date
  },

  // MySQL 8.0
  {
    id: 'addinst_mysql-1',
    addon_id: 'addon_014b67a9-994e-480f-8f30-993000c2fe88',
    cluster_id: '',
    version: '8.0',
    primary_component: {
      image: {
        registry_url: 'mysql',
        tag: '8.0',
      },
    },
    resources: [
      {
        name: 'mysql',
        type: 'StatefulSet',
        namespace: 'mysql',
        images: [
          {
            registry_url: 'mysql',
            tag: '8.0',
          },
        ],
      },
    ],
    // no eol date
  },

  // Fluent Bit GKE Exporter v0.26.1-gke.0
  {
    id: 'addinst_fluentbit-gke-exporter-1',
    addon_id: 'addon_03972bf4-881b-4e38-b341-2bb95de34438',
    cluster_id: 'id-gke-127-usw4-dev',
    version: 'v0.26.1-gke.0',
    primary_component: {
      image: {
        registry_url: 'gcr.io/fluentbit/fluent-bit-gke-exporter',
        tag: 'v0.26.1-gke.0',
      },
    },
    resources: [
      {
        name: 'fluentbit-gke-exporter',
        type: 'DaemonSet',
        namespace: 'fluentbit',
        images: [
          {
            registry_url: 'gcr.io/fluentbit/fluent-bit-gke-exporter',
            tag: 'v0.26.1-gke.0',
          },
        ],
      },
    ],
    // no eol date
  },

  // GKE Metrics Agent v1.12.3-gke.2
  {
    id: 'addinst_gke-metrics-agent-1',
    addon_id: 'addon_03ba59e2-9966-431e-926d-1ce02cd5f75f',
    cluster_id: 'id-gke-131-usw4-sandbox',
    version: '1.12.3-gke.2',
    primary_component: {
      image: {
        registry_url: 'gcr.io/google-containers/gke-metrics-agent',
        tag: '1.12.3-gke.2',
      },
    },
    resources: [
      {
        name: 'gke-metrics-agent',
        type: 'Deployment',
        namespace: 'gke-system',
        images: [
          {
            registry_url: 'gcr.io/google-containers/gke-metrics-agent',
            tag: '1.12.3-gke.2',
          },
        ],
      },
    ],
    // no eol date
  },

  // KAS Network Proxy v0.30.4-gke.0
  {
    id: 'addinst_kas-network-proxy-1',
    addon_id: 'addon_04f3c9f2-79e6-4d57-9e08-8f0f94f25d0f',
    cluster_id: 'id-gke-131-usw4-sandbox',
    version: 'v0.30.4-gke.0',
    primary_component: {
      image: {
        registry_url: 'gcr.io/google-containers/kas-network-proxy',
        tag: 'v0.30.4-gke.0',
      },
    },
    resources: [
      {
        name: 'kas-network-proxy',
        type: 'Deployment',
        namespace: 'kube-system',
        images: [
          {
            registry_url: 'gcr.io/google-containers/kas-network-proxy',
            tag: 'v0.30.4-gke.0',
          },
        ],
      },
    ],
    // no eol date
  },

  // Prometheus Operator v0.67.1
  {
    id: 'addinst_prometheus-operator-1',
    addon_id: 'addon_07ca056a-e803-465a-873b-64e708d4b311',
    cluster_id: '',
    version: 'v0.67.1',
    primary_component: {
      image: {
        registry_url: 'quay.io/prometheus-operator/prometheus-operator',
        tag: 'v0.67.1',
      },
    },
    resources: [
      {
        name: 'prometheus-operator',
        type: 'Deployment',
        namespace: 'monitoring',
        images: [
          {
            registry_url: 'quay.io/prometheus-operator/prometheus-operator',
            tag: 'v0.67.1',
          },
        ],
      },
    ],
    // no eol date
  },

  // Prometheus v2.53.1
  {
    id: 'addinst_prometheus-1',
    addon_id: 'addon_7f1e198a-c2e0-446b-8567-f7154dfb38de',
    cluster_id: '',
    version: 'v2.53.1',
    primary_component: {
      image: {
        registry_url: 'prom/prometheus',
        tag: 'v2.53.1',
      },
    },
    resources: [
      {
        name: 'prometheus',
        type: 'Deployment',
        namespace: 'monitoring',
        images: [
          {
            registry_url: 'quay.io/prometheus/prometheus',
            tag: 'v2.53.1',
          },
        ],
      },
      {
        name: 'prometheus-a32b576f98',
        type: 'ReplicaSet',
        namespace: 'monitoring',
        images: [
          {
            registry_url: 'quay.io/prometheus/prometheus',
            tag: 'v2.53.1',
          },
        ],
      },
    ],
    eol_date: new Date(2025, 6, 31).getTime() / 1000, // 2025-07-31
  },

  // Kyverno OSS v1.10.3
  {
    id: 'addinst_kyverno-oss-1',
    addon_id: 'addon_0dfe7453-67f3-446e-bfd9-ec047cb656b3',
    cluster_id: '',
    version: 'v1.10.3',
    primary_component: {
      image: {
        registry_url: 'ghcr.io/kyverno/kyverno',
        tag: 'v1.10.3',
      },
    },
    resources: [
      {
        name: 'kyverno',
        type: 'Deployment',
        namespace: 'kyverno',
        images: [
          {
            registry_url: 'ghcr.io/kyverno/kyverno',
            tag: 'v1.10.3',
          },
        ],
      },
    ],
    eol_date: new Date(2024, 9, 29).getTime() / 1000, // 2024-10-29
  },
  {
    id: 'addinst_kyverno-oss-2',
    addon_id: 'addon_0dfe7453-67f3-446e-bfd9-ec047cb656b3',
    cluster_id: 'id-eks-130-use2-staging',
    version: '1.12.3',
    primary_component: {
      image: {
        registry_url: 'ghcr.io/kyverno/kyverno',
        tag: '1.12.3',
      },
    },
    resources: [
      {
        name: 'kyverno',
        type: 'Deployment',
        namespace: 'kyverno',
        images: [
          {
            registry_url: 'ghcr.io/kyverno/kyverno',
            tag: '1.12.3',
          },
        ],
      },
    ],
    eol_date: new Date(2025, 5, 30).getTime() / 1000, // 2025-06-30
  },

  // Grafana Agent v0.34.1
  {
    id: 'addinst_grafana-agent-1',
    addon_id: 'addon_0f9c0f58-456f-4c98-98d0-92c8f00d367e',
    cluster_id: '',
    version: 'v0.34.1',
    primary_component: {
      image: {
        registry_url: 'grafana/agent',
        tag: 'v0.34.1',
      },
    },
    resources: [
      {
        name: 'grafana-agent',
        type: 'Deployment',
        namespace: 'grafana',
        images: [
          {
            registry_url: 'grafana/agent',
            tag: 'v0.34.1',
          },
        ],
      },
    ],
    // no eol date
  },

  // Grafana Loki 2.8.4
  {
    id: 'addinst_grafana-loki-1',
    addon_id: 'addon_1a2b3c4d-5e6f-7g8h-80b5-9i10j11k12l1',
    cluster_id: '',
    version: '2.8.4',
    primary_component: {
      image: {
        registry_url: 'grafana/loki',
        tag: '2.8.4',
      },
    },
    resources: [
      {
        name: 'loki',
        type: 'StatefulSet',
        namespace: 'loki',
        images: [
          {
            registry_url: 'grafana/loki',
            tag: '2.8.4',
          },
        ],
      },
    ],
    // no eol date
  },

  // GCP Prometheus Engine v2.45.3-gmp.8-gke.0
  {
    id: 'addinst_gcp-prometheus-engine-1',
    addon_id: 'addon_1aa21c41-ef27-4569-97ca-e6827b12c7d9',
    cluster_id: 'id-gke-129-euw1-prod',
    version: 'v2.45.3-gmp.8-gke.0',
    primary_component: {
      image: {
        registry_url: 'gcr.io/gke-release/prometheus-engine',
        tag: 'v2.45.3-gmp.8-gke.0',
      },
    },
    resources: [
      {
        name: 'gcp-prometheus-engine',
        type: 'Deployment',
        namespace: 'monitoring',
        images: [
          {
            registry_url: 'gcr.io/gke-release/prometheus-engine',
            tag: 'v2.45.3-gmp.8-gke.0',
          },
        ],
      },
    ],
    // no eol date
  },

  // Redis OSS v7.2.4-alpine
  {
    id: 'addinst_redis-oss-1',
    addon_id: 'addon_1cb203e4-497c-4842-98f1-337e2a2ec051',
    cluster_id: '',
    version: '7.2.4-alpine',
    primary_component: {
      image: {
        registry_url: 'docker.io/redis',
        tag: '7.2.4-alpine',
      },
    },
    resources: [
      {
        name: 'redis',
        type: 'StatefulSet',
        namespace: 'redis',
        images: [
          {
            registry_url: 'docker.io/redis',
            tag: '7.2.4-alpine',
          },
        ],
      },
    ],
    // no eol date
  },

  // ExternalDNS 0.14.2-debian-12-r8
  {
    id: 'addinst_externaldns-0-14-2',
    addon_id: 'addon_42aac86e-d054-4a44-9d80-d74ba8c41027',
    cluster_id: '',
    version: '0.14.2-debian-12-r8',
    primary_component: {
      image: {
        registry_url: 'k8s.gcr.io/external-dns/external-dns',
        tag: '0.14.2-debian-12-r8',
      },
    },
    resources: [
      {
        name: 'external-dns',
        type: 'Deployment',
        namespace: 'external-dns',
        images: [
          {
            registry_url: 'k8s.gcr.io/external-dns/external-dns',
            tag: '0.14.2-debian-12-r8',
          },
        ],
      },
    ],
    // no eol date
  },

  // Redis Enterprise 6.4.2
  {
    id: 'addinst_redis-enterprise-1',
    addon_id: 'addon_acff6ed6-0db7-4bd7-8f22-26d0cc72a12e',
    cluster_id: '',
    version: '6.4.2',
    primary_component: {
      image: {
        registry_url: 'redislabs/redis',
        tag: '6.4.2',
      },
    },
    resources: [
      {
        name: 'redis-enterprise',
        type: 'StatefulSet',
        namespace: 'redis',
        images: [
          {
            registry_url: 'docker.io/redislabs',
            tag: '6.4.2',
          },
        ],
      },
    ],
    eol_date: new Date(2025, 7, 31).getTime() / 1000, // 2025-08-31
  },

  // KEDA 2.15.1
  {
    id: 'addinst_keda-1',
    addon_id: 'addon_304813ed-5423-4a5e-8783-49c7af10572e',
    cluster_id: '',
    version: '2.15.1',
    primary_component: {
      image: {
        registry_url: 'ghcr.io/kedacore/keda',
        tag: '2.15.1',
      },
    },
    resources: [
      {
        name: 'keda-operator',
        type: 'Deployment',
        namespace: 'keda',
        images: [
          {
            registry_url: 'ghcr.io/kedacore/keda',
            tag: '2.15.1',
          },
        ],
      },
    ],
    // EOL: Sep 28, 2023 (per [KEDA releases](https://github.com/kedacore/keda/releases))
    eol_date: new Date(2023, 8, 28).getTime() / 1000,
  },

  // Alertmanager v0.27.0
  {
    id: 'id-addon_alertmanager-0-27-0',
    addon_id: 'addon_d6e318d9-5802-46a4-a497-a6ffa7f8c57f',
    cluster_id: '',
    version: 'v0.27.0',
    primary_component: {
      image: {
        registry_url: 'quay.io/prometheus/alertmanager',
        tag: 'v0.27.0',
      },
    },
    resources: [
      {
        name: 'alertmanager',
        type: 'StatefulSet',
        namespace: 'monitoring',
        images: [
          {
            registry_url: 'quay.io/prometheus/alertmanager',
            tag: 'v0.27.0',
          },
        ],
      },
    ],
    // no eol date
  },

  // ClickHouse 24.8.2
  {
    id: 'addinst_clickhouse-1',
    addon_id: 'addon_3fef5525-68df-4064-a534-b7c819e4d205',
    cluster_id: '',
    version: '24.8.2',
    primary_component: {
      image: {
        registry_url: 'clickhouse/clickhouse-server',
        tag: '24.8.2',
      },
    },
    resources: [
      {
        name: 'keeper',
        type: 'StatefulSet',
        namespace: 'clickhouse',
        images: [
          {
            registry_url: 'docker.io/clickhouse/clickhouse-keeper',
            tag: '24.8.2',
          },
        ],
      },
    ],
    eol_date: new Date(2025, 7, 21).getTime() / 1000, // 2025-08-21
  },

  // CockroachDB v24.1.3 & v23.2.6
  {
    id: 'addinst_cockroachdb-1',
    addon_id: 'addon_e96a31e9-2a67-4e71-ab92-b13cd1c105be',
    cluster_id: '',
    version: 'v24.1.3',
    primary_component: {
      image: {
        registry_url: 'cockroachdb/cockroach',
        tag: 'v24.1.3',
      },
    },
    resources: [
      {
        name: 'cockroachdb',
        type: 'StatefulSet',
        namespace: 'cockroachdb',
        images: [
          {
            registry_url: 'cockroachdb/cockroach',
            tag: 'v24.1.3',
          },
        ],
      },
    ],
    // EOL: Dec 20, 2025 (using provided earliest_eol_date as 1763596800)
    eol_date: new Date(2025, 11, 20).getTime() / 1000,
  },
  {
    id: 'addinst_cockroachdb-2',
    addon_id: 'addon_e96a31e9-2a67-4e71-ab92-b13cd1c105be',
    cluster_id: '',
    version: '23.2.6', // or "23.2.0-23.2.6" if multiple supported, pick one for tag
    primary_component: {
      image: {
        registry_url: 'cockroachdb/cockroach',
        tag: 'v23.2.6',
      },
    },
    resources: [
      {
        name: 'cockroachdb',
        type: 'StatefulSet',
        namespace: 'cockroachdb',
        images: [
          {
            registry_url: 'cockroachdb/cockroach',
            tag: 'v23.2.6',
          },
        ],
      },
    ],
    eol_date: new Date(2025, 7, 5).getTime() / 1000, // 2025-08-05
  },

  // Gloo Edge Enterprise v1.14.9
  {
    id: 'addinst_gloo-ee-1',
    addon_id: 'addon_6c19490d-82e5-4e16-b564-8d2027daa2da',
    cluster_id: 'id-eks-130-use2-staging',
    version: '1.14.9',
    primary_component: {
      image: {
        registry_url: 'quay.io/solo-io/gloo-ee',
        tag: '1.14.9',
      },
    },
    resources: [
      {
        name: 'gateway-proxy',
        type: 'Deployment',
        namespace: 'gloo-system',
        images: [
          {
            registry_url: 'quay.io/solo-io/gateway-proxy',
            tag: '1.14.9',
          },
        ],
      },
      {
        name: 'gloo-fed-console',
        type: 'Deployment',
        namespace: 'gloo-system',
        images: [
          {
            registry_url: 'quay.io/solo-io/gloo-fed-console',
            tag: '1.14.9',
          },
        ],
      },
      {
        name: 'gloo-fed',
        type: 'Deployment',
        namespace: 'gloo-system',
        images: [
          {
            registry_url: 'quay.io/solo-io/gloo-fed',
            tag: '1.14.9',
          },
        ],
      },
      {
        name: 'gloo-ee',
        type: 'Deployment',
        namespace: 'gloo-system',
        images: [
          {
            registry_url: 'quay.io/solo-io/gloo-ee',
            tag: '1.14.9',
          },
        ],
      },
      {
        name: 'private-proxy',
        type: 'Deployment',
        namespace: 'gloo-system',
        images: [
          {
            registry_url: 'quay.io/solo-io/private-proxy',
            tag: '1.14.9',
          },
        ],
      },
      {
        name: 'rbac-validating-webhook',
        type: 'Deployment',
        namespace: 'gloo-system',
        images: [
          {
            registry_url: 'quay.io/solo-io/rbac-validating-webhook',
            tag: '1.14.9',
          },
        ],
      },
    ],
    eol_date: new Date(2024, 11, 12).getTime() / 1000, // 2024-12-12
  },
];

const clusterIds = CLUSTERS.map((c) => c.id);
distributeClusterIdsToAddonInstances(clusterIds, ADDONS);
